/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import { Directive, ElementRef, Inject, Input, booleanAttribute, numberAttribute } from '@angular/core';
import { Nullable } from '@core/types/nullable.type';
import { CHIP } from '../variables/chip.variable';

/**
 * Section within a chip.
 * @docs-private
 */
@Directive({
  selector: '[coreChipAction]',
  host: {
    class: 'chip__action chip-action',
    '[class.chip__action--primary]': 'isPrimary',
    '[class.chip__action--presentational]': '!isInteractive',
    '[class.chip__action--trailing]': '!isPrimary',
    '[attr.tabindex]': 'getTabindex()',
    '[attr.disabled]': 'getDisabledAttribute()',
    '[attr.aria-disabled]': 'disabled',
    '(click)': 'handleClick($event)',
    '(keydown)': 'handleKeydown($event)',
  },
})
export class ChipActionDirective {
  /**
   * Whether the action is interactive
   */
  @Input()
  isInteractive = true;

  /**
   * Whether this is the primary action in the chip
   */
  isPrimary = true;

  /**
   * Whether the action is disabled
   */
  @Input({ transform: booleanAttribute })
  get disabled(): boolean {
    return this._disabled || this.parentChip.disabled;
  }

  set disabled(value: boolean) {
    this._disabled = value;
  }

  private _disabled = false;

  /**
   * Tab index of the action
   */
  @Input({
    transform: (value: unknown) => (value == null ? -1 : numberAttribute(value)),
  })
  tabIndex: number = -1;

  /**
   * Private API to allow focusing this chip when it is disabled.
   */
  @Input()
  private allowFocusWhenDisabled = false;

  constructor(
    public elementRef: ElementRef<HTMLElement>,
    @Inject(CHIP)
    protected parentChip: {
      handlePrimaryActionInteraction(): void;
      remove(): void;
      disabled: boolean;
      isEditing?: boolean;
    },
  ) {
    if (elementRef.nativeElement.nodeName === 'BUTTON') {
      elementRef.nativeElement.setAttribute('type', 'button');
    }
  }

  /**
   * Determine the value of the disabled attribute for this chip action.
   */
  protected getDisabledAttribute(): Nullable<string> {
    // When this chip action is disabled and focusing disabled chips is not permitted, return empty
    // string to indicate that disabled attribute should be included.
    return this.disabled && !this.allowFocusWhenDisabled ? '' : null;
  }

  /**
   * Determine the value of the tabindex attribute for this chip action.
   */
  protected getTabindex(): Nullable<string> {
    return (this.disabled && !this.allowFocusWhenDisabled) || !this.isInteractive ? null : this.tabIndex.toString();
  }

  focus(): void {
    this.elementRef.nativeElement.focus();
  }

  handleClick(event: MouseEvent): void {
    if (!this.disabled && this.isInteractive && this.isPrimary) {
      event.preventDefault();
      this.parentChip.handlePrimaryActionInteraction();
    }
  }

  handleKeydown(event: KeyboardEvent): void {
    if (
      (event.key === 'Enter' || event.key === ' ') &&
      !this.disabled &&
      this.isInteractive &&
      this.isPrimary &&
      !this.parentChip.isEditing
    ) {
      event.preventDefault();
      this.parentChip.handlePrimaryActionInteraction();
    }
  }
}
